import { useQuery } from '@tanstack/react-query';
import {
  formatMoney,
  formatQuantity,
  fromCents,
  type JobInstallment,
  type JobInstallmentLineRequest,
  type JobPricingResponse,
  LICENCE_TYPE_CODE,
  type LicenceConfig,
  type LicenceLinesRequest,
  licenceQuote,
  parseDecimal,
  type PriceListResponse,
  quoteTotals,
  toCents,
  typeTree,
} from '@k-management/shared';
import { useState } from 'react';
import { DecimalInput } from '../../components/DecimalInput.js';
import { apiRequest } from '../../lib/api.js';
import { LicenceDialog } from '../quotes/LicenceDialog.js';
import { jobKeys } from './jobs-ui.js';

/** A line of the rata as it is being written (2.50). */
export interface LineDraft {
  key: number;
  typeCode: string;
  subTypeCode: string;
  description: string;
  editions: string;
  quantity: string;
  unitCode: string;
  unitPrice: string;
  discountPercent: string;
  /** A spesa (2.52): out of the margin. */
  isExpense: boolean;
}

let nextKey = 0;

export function lineDraft(fields: Partial<Omit<LineDraft, 'key'>> = {}): LineDraft {
  return {
    key: nextKey++,
    typeCode: '',
    subTypeCode: '',
    description: '',
    editions: '1',
    quantity: '1',
    unitCode: '',
    unitPrice: '',
    discountPercent: '0',
    isExpense: false,
    ...fields,
  };
}

/** The lines of a rata saved, to write them again; the licence lines come from the configurator. */
export function draftsOf(installment: JobInstallment): LineDraft[] {
  return installment.lines
    .filter((line) => line.typeCode !== LICENCE_TYPE_CODE)
    .map((line) =>
      lineDraft({
        typeCode: line.typeCode,
        subTypeCode: line.subTypeCode ?? '',
        description: line.description ?? '',
        editions: String(line.editions),
        quantity: String(Number(line.quantity)),
        unitCode: line.unitCode,
        unitPrice: line.unitPrice,
        discountPercent: line.finalAmount !== null ? '0' : String(Number(line.discountPercent)),
        isExpense: line.isExpense,
      }),
    );
}

export const pricingKey = (jobId: string) => [...jobKeys.all, 'pricing', jobId] as const;

export function usePricing(jobId: string) {
  return useQuery({
    queryKey: pricingKey(jobId),
    queryFn: () => apiRequest<JobPricingResponse>(`/jobs/${jobId}/pricing`),
    staleTime: 60_000,
  });
}

/** The price of the listino of the client for a line: the sub-type first, then the whole type. */
function listedPrice(pricing: JobPricingResponse, line: LineDraft): string | null {
  const match = (subTypeCode: string | null) =>
    pricing.prices.find(
      (item) =>
        item.typeCode === line.typeCode &&
        item.unitCode === line.unitCode &&
        item.subTypeCode === subTypeCode,
    );
  return (match(line.subTypeCode || null) ?? match(null))?.price ?? null;
}

/** The request of a line, as the API reads it. */
export function lineRequest(line: LineDraft): JobInstallmentLineRequest {
  return {
    typeCode: line.typeCode,
    subTypeCode: line.subTypeCode || null,
    description: line.description.trim() || null,
    editions: Math.max(1, Math.trunc(parseDecimal(line.editions) ?? 1)),
    quantity: line.quantity,
    unitCode: line.unitCode,
    unitPrice: line.unitPrice,
    discountPercent: line.discountPercent || 0,
    isExpense: line.isExpense,
  };
}

/** What the lines and the licences make: each line's amount and the rata total. */
export function linesTotal(
  lines: readonly LineDraft[],
  licences: LicenceConfig | null,
  priceList: PriceListResponse | undefined,
) {
  const inputs = lines.map((line) => ({
    editions: Math.max(1, Math.trunc(parseDecimal(line.editions) ?? 1)),
    quantity: parseDecimal(line.quantity) ?? 0,
    unitPrice: parseDecimal(line.unitPrice) ?? 0,
    discountPercent: parseDecimal(line.discountPercent) ?? 0,
    vatCode: '',
  }));
  const licenceLines =
    licences && priceList?.tiers.length
      ? licenceQuote(licences, priceList.tiers).lines.map((line) => {
          const discount = licences.lineDiscounts[line.key];
          return {
            label: line.label,
            input: {
              quantity: 1,
              unitPrice: line.amount,
              discountPercent: discount?.finalAmount ? 0 : (discount?.discountPercent ?? 0),
              finalAmount: discount?.finalAmount ?? null,
              premium: line.premium,
              vatCode: '',
            },
          };
        })
      : [];
  const totals = quoteTotals([...inputs, ...licenceLines.map((line) => line.input)]);
  const expenses = totals.lines
    .slice(0, lines.length)
    .filter((_, index) => lines[index]!.isExpense)
    .reduce((sum, line) => sum + toCents(line.net), 0);
  return {
    expenses: fromCents(expenses),
    lines: totals.lines.slice(0, lines.length).map((line) => line.net),
    licences: licenceLines.map((line, index) => ({
      label: line.label,
      net: totals.lines[lines.length + index]!.net,
    })),
    total: totals.linesNet,
  };
}

/**
 * The lines of a rata «dalle tipologie» (2.50, 28/10/2026): written like the
 * lines of a preventivo, the price proposed by the listino of the client and
 * always writable; the licences through the configurator of the preventivo.
 */
export function RataLines({
  jobId,
  lines,
  onLines,
  licences,
  onLicences,
  locked,
}: {
  jobId: string;
  lines: LineDraft[];
  onLines: (lines: LineDraft[]) => void;
  licences: LicenceConfig | null;
  onLicences: (licences: LicenceConfig | null) => void;
  /** The rata has invoices: its lines stay as they are. */
  locked: boolean;
}) {
  const pricing = usePricing(jobId);
  const [configuring, setConfiguring] = useState(false);
  if (pricing.isPending) return <p className="muted">Caricamento delle tipologie…</p>;
  if (pricing.isError) return <p className="error">{pricing.error.message}</p>;
  const data = pricing.data;
  const tree = typeTree(data.types).filter((item) => item.type.code !== LICENCE_TYPE_CODE);
  const unitOf = (code: string) =>
    data.units.find((unit) => unit.code === code)?.description ?? code;
  const defaultUnit = data.units.find((unit) => unit.isDefault)?.code ?? data.units[0]?.code ?? '';
  const totals = linesTotal(lines, licences, data.priceList);

  const update = (key: number, change: Partial<LineDraft>) =>
    onLines(
      lines.map((line) => {
        if (line.key !== key) return line;
        const next = { ...line, ...change };
        // A new voice or unit brings the price of the listino, unless one was written by hand.
        if ('typeCode' in change || 'subTypeCode' in change || 'unitCode' in change) {
          const before = listedPrice(data, line);
          const after = listedPrice(data, next);
          const untouched =
            line.unitPrice.trim() === '' ||
            (before !== null && parseDecimal(line.unitPrice) === parseDecimal(before));
          if (untouched && after !== null) next.unitPrice = after;
        }
        return next;
      }),
    );
  const chooseType = (key: number, typeCode: string) => {
    const type = data.types.find((item) => item.code === typeCode);
    update(key, { typeCode, subTypeCode: '', unitCode: type?.unit ?? defaultUnit });
  };
  const chooseSubType = (key: number, subTypeCode: string) => {
    const sub = data.types.find((item) => item.code === subTypeCode);
    const line = lines.find((item) => item.key === key)!;
    update(key, { subTypeCode, unitCode: sub?.unit ?? line.unitCode });
  };

  return (
    <div className="stack">
      <p className="muted small">
        Prezzi dal listino attività <strong>{data.activityList.name}</strong>
        {data.activityList.code ? ' del cliente' : ''}: si possono sempre scrivere a mano.
        L’imponibile della rata è la somma delle righe.
      </p>
      <div className="table-scroll">
        <table className="lines rata-lines">
          <thead>
            <tr>
              <th>Tipologia</th>
              <th>Di che tipo</th>
              <th className="right">Edizioni</th>
              <th className="right">Q.tà</th>
              <th>U.M.</th>
              <th className="right">Prezzo</th>
              <th className="right">Sconto %</th>
              <th className="right">Importo</th>
              <th title="Trasferte, vitto, rimborsi: fuori dal margine">Spesa</th>
              {!locked && <th />}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const children =
                tree.find((item) => item.type.code === line.typeCode)?.children ?? [];
              const chosen =
                data.types.find((item) => item.code === (line.subTypeCode || line.typeCode)) ??
                null;
              const listed = listedPrice(data, line);
              const changed =
                listed !== null && parseDecimal(line.unitPrice) !== parseDecimal(listed);
              return (
                <tr key={line.key}>
                  <td>
                    <select
                      value={line.typeCode}
                      disabled={locked}
                      aria-label="Tipologia"
                      onChange={(event) => chooseType(line.key, event.target.value)}
                    >
                      <option value="">Scegli…</option>
                      {tree.map((item) => (
                        <option key={item.type.code} value={item.type.code}>
                          {item.type.description}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="stack tight">
                      {children.length > 0 && (
                        <select
                          value={line.subTypeCode}
                          disabled={locked}
                          aria-label="Di che tipo"
                          onChange={(event) => chooseSubType(line.key, event.target.value)}
                        >
                          <option value="">Scegli…</option>
                          {children.map((child) => (
                            <option key={child.code} value={child.code}>
                              {child.description}
                            </option>
                          ))}
                        </select>
                      )}
                      {(chosen?.freeText || line.description) && (
                        <input
                          value={line.description}
                          disabled={locked}
                          maxLength={300}
                          aria-label="Testo della riga"
                          placeholder={chosen?.freeText ? 'Che cosa comprende (facoltativo)' : ''}
                          onChange={(event) =>
                            update(line.key, { description: event.target.value })
                          }
                        />
                      )}
                    </div>
                  </td>
                  <td>
                    <DecimalInput
                      className="number"
                      decimals={[0, 0]}
                      value={line.editions}
                      disabled={locked}
                      aria-label="Edizioni"
                      onChange={(value) => update(line.key, { editions: value })}
                    />
                  </td>
                  <td>
                    <DecimalInput
                      className="number"
                      decimals={[0, 3]}
                      value={line.quantity}
                      disabled={locked}
                      aria-label="Quantità"
                      onChange={(value) => update(line.key, { quantity: value })}
                    />
                  </td>
                  <td>
                    <select
                      value={line.unitCode}
                      disabled={locked}
                      aria-label="Unità"
                      onChange={(event) => update(line.key, { unitCode: event.target.value })}
                    >
                      {data.units.map((unit) => (
                        <option key={unit.code} value={unit.code}>
                          {unit.description}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <DecimalInput
                      className="number"
                      decimals={[2, 2]}
                      value={line.unitPrice}
                      disabled={locked}
                      aria-label="Prezzo"
                      onChange={(value) => update(line.key, { unitPrice: value })}
                    />
                    {changed && (
                      <small className="muted block">listino {formatMoney(listed)}</small>
                    )}
                  </td>
                  <td>
                    <DecimalInput
                      className="number"
                      decimals={[0, 2]}
                      value={line.discountPercent}
                      disabled={locked}
                      aria-label="Sconto %"
                      onChange={(value) => update(line.key, { discountPercent: value })}
                    />
                  </td>
                  <td className="right nowrap">
                    {formatMoney(totals.lines[index] ?? '0')}
                    {Number(line.editions) > 1 && (
                      <small className="muted block">
                        {line.editions} × {formatQuantity(parseDecimal(line.quantity) ?? 0)}{' '}
                        {unitOf(line.unitCode).toLowerCase()}
                      </small>
                    )}
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={line.isExpense}
                      aria-label="Spesa"
                      onChange={(event) => update(line.key, { isExpense: event.target.checked })}
                    />
                  </td>
                  {!locked && (
                    <td>
                      <button
                        type="button"
                        className="link"
                        onClick={() => onLines(lines.filter((item) => item.key !== line.key))}
                      >
                        Togli
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {totals.licences.map((line) => (
              <tr key={line.label}>
                <td colSpan={7}>
                  Licenze — {line.label}
                  <small className="muted"> · dal configuratore</small>
                </td>
                <td className="right nowrap">{formatMoney(line.net)}</td>
                <td />
                {!locked && <td />}
              </tr>
            ))}
            <tr>
              <td colSpan={7} className="muted">
                Imponibile della rata
              </td>
              <td className="right nowrap">
                <strong>{formatMoney(totals.total)}</strong>
                {toCents(totals.expenses) !== 0 && (
                  <small className="muted block">di cui spese {formatMoney(totals.expenses)}</small>
                )}
              </td>
              <td />
              {!locked && <td />}
            </tr>
          </tbody>
        </table>
      </div>
      {!locked && (
        <div className="row">
          <button
            type="button"
            className="link"
            onClick={() => onLines([...lines, lineDraft({ unitCode: defaultUnit })])}
          >
            + Un’altra riga
          </button>
          <button type="button" className="link" onClick={() => setConfiguring(true)}>
            {licences ? 'Licenze: cambia…' : '+ Licenze…'}
          </button>
          {licences && (
            <button type="button" className="link" onClick={() => onLicences(null)}>
              Togli le licenze
            </button>
          )}
        </div>
      )}
      {locked && (
        <p className="muted small">La rata ha già fatture: le sue righe non si cambiano.</p>
      )}
      {configuring && (
        <LicenceDialog
          before={licences}
          hasLicences={licences !== null}
          canDiscount
          priceListPath="/jobs/licence-price-list"
          where="rata"
          onClose={() => setConfiguring(false)}
          onSave={async (body: LicenceLinesRequest) => {
            onLicences(licenceConfigOf(body));
            setConfiguring(false);
          }}
        />
      )}
    </div>
  );
}

/** What the configurator asked for, kept in the rata until it is saved. */
function licenceConfigOf(body: LicenceLinesRequest): LicenceConfig {
  const discounts = body.lineDiscounts ?? {};
  return {
    pax: Number(body.pax),
    withSetup: body.withSetup ?? true,
    renewalDiscount:
      body.renewalDiscount === null || body.renewalDiscount === undefined
        ? null
        : String(body.renewalDiscount),
    months: Number(body.months ?? 12),
    extraSetup: body.extraSetup
      ? { description: body.extraSetup.description, amount: body.extraSetup.amount }
      : null,
    lineDiscounts: Object.fromEntries(
      Object.entries(discounts).flatMap(([key, value]) =>
        value
          ? [
              [
                key,
                {
                  discountPercent: String(value.discountPercent ?? 0),
                  finalAmount:
                    value.finalAmount === null || value.finalAmount === undefined
                      ? null
                      : String(value.finalAmount),
                },
              ],
            ]
          : [],
      ),
    ),
  };
}

/**
 * How a rata or a cost is made (2.54): two radio buttons, each with what it
 * means, instead of a bar that did not read as a choice.
 */
export function ModeChoice<T extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string;
  value: T;
  options: { value: T; label: string; hint: string; disabled?: boolean }[];
  onChange: (value: T) => void;
}) {
  return (
    <fieldset className="mode-choice">
      <legend>{legend}</legend>
      {options.map((option) => (
        <label key={option.value} className={option.value === value ? 'selected' : undefined}>
          <input
            type="radio"
            checked={option.value === value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
          />
          <span>
            <strong>{option.label}</strong>
            <small className="muted block">{option.hint}</small>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** «Per salvare manca: …» under a disabled Salva (2.54). */
export function MissingNote({ missing }: { missing: string[] }) {
  if (!missing.length) return null;
  return <p className="muted small">Per salvare manca: {missing.join(', ')}.</p>;
}
